/**
 * Small presentational building blocks shared by every report's `ReportGuideAccordion`
 * content (see `routes/reportGuides.tsx` and `DailyPulseScreen.tsx`) — the coloured
 * highlight-card grid and formula list `ecom_code`'s own guide notes use. Kept as plain
 * hard-coded Tailwind colours (not this platform's semantic tokens) DELIBERATELY: this is
 * copied CONTENT, not structural chrome, and the whole point is to look like the same
 * component as legacy's guide cards.
 */

export const noteText = "text-[13px] leading-relaxed";

export function HighlightGrid({
  children,
  cols = 3,
}: {
  readonly children: React.ReactNode;
  readonly cols?: 2 | 3 | 4;
}): React.JSX.Element {
  return (
    <div
      className={`my-3 grid grid-cols-1 gap-3 ${
        cols === 2 ? "md:grid-cols-2" : cols === 4 ? "sm:grid-cols-2 lg:grid-cols-4" : "md:grid-cols-3"
      }`}
    >
      {children}
    </div>
  );
}

export type HighlightTone = "blue" | "purple" | "emerald" | "amber" | "indigo";

const TONES: Record<HighlightTone, string> = {
  blue: "bg-blue-50/60 border-blue-100 text-blue-900 dark:bg-blue-500/10 dark:border-blue-900/40 dark:text-blue-300",
  purple: "bg-purple-50/60 border-purple-100 text-purple-900 dark:bg-purple-500/10 dark:border-purple-900/40 dark:text-purple-300",
  emerald:
    "bg-emerald-50/60 border-emerald-100 text-emerald-900 dark:bg-emerald-500/10 dark:border-emerald-900/40 dark:text-emerald-300",
  amber: "bg-amber-50/70 border-amber-200/80 text-amber-900 dark:bg-amber-500/10 dark:border-amber-900/40 dark:text-amber-300",
  indigo:
    "bg-indigo-50/70 border-indigo-200/80 text-indigo-900 dark:bg-indigo-500/10 dark:border-indigo-900/40 dark:text-indigo-300",
};

export function HighlightCard({
  tone,
  title,
  icon,
  children,
}: {
  readonly tone: HighlightTone;
  readonly title: string;
  readonly icon?: React.ReactNode;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className={`flex flex-col gap-1 rounded-xl border p-3 ${TONES[tone]}`}>
      <strong className="flex items-center gap-1.5 text-xs">
        {icon}
        {title}
      </strong>
      <p className="text-muted-foreground text-xs">{children}</p>
    </div>
  );
}

export function FormulaList({ children }: { readonly children: React.ReactNode }): React.JSX.Element {
  return <ul className={`${noteText} m-0 list-disc space-y-1.5 pl-[18px]`}>{children}</ul>;
}
